import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MarketingLayout from "./MarketingLayout";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useI18n } from "@/lib/i18n";
import { PricingRow } from "@/components/marketing/PricingRow";
import TestimonialRotator from "@/components/TestimonialRotator";

const Feature = ({ eyebrow, title, body, cta }) => (
  <div className="py-10">
    <div className="label-tiny mb-3">{eyebrow}</div>
    <h3 className="font-display-italic text-2xl md:text-3xl leading-[1.05] mb-3 tracking-tight">
      {title}
    </h3>
    {/* Body accepts either a string or JSX (e.g. multiple paragraphs).
        Wrapped in a div, not a p, so callers can pass <p>…</p><p>…</p>
        without nesting paragraphs (invalid HTML). When given a string we
        still render a single semantic paragraph. */}
    {typeof body === "string" ? (
      <p className="font-body-editorial text-base md:text-[17px] text-[color:var(--ink-2)] max-w-xl leading-relaxed">
        {body}
      </p>
    ) : (
      <div className="font-body-editorial text-base md:text-[17px] text-[color:var(--ink-2)] max-w-xl leading-relaxed space-y-3">
        {body}
      </div>
    )}
    {cta && (
      <Link
        to={cta.to}
        className="inline-block mt-4 text-[15px] underline underline-offset-4 decoration-[color:var(--ink-3)] hover:decoration-[color:var(--ink)] text-[color:var(--ink)]"
        data-testid={cta.testId}
      >
        {cta.label} →
      </Link>
    )}
  </div>
);


// ---- Landing dashboard rotator ----
// Cycles through three mini-dashboard views (mood / calendar / pets) every
// six seconds, so first-time visitors see all three "flavours" of FamFeel
// without scrolling. Tap the dots to jump directly to one. Pauses on
// hover/focus so a reader isn't yanked mid-glance.
// Rotator order — month grid first per the user's explicit request,
// then a tour through the other surfaces of the app:
//   1. Calendar month grid (the front door for new families)
//   2. What's left of today (mood-front dashboard with Happening Now)
//   3. Display Mode (kitchen-iPad ambient view)
//   4. Event detail (Lemon's birthday with When + Notes)
//   5. Packing list (shared list with per-member assignment)
// Filenames live in /public/landing/ as semantic .jpg slugs so the
// folder reads at a glance.
const ROTATOR_VIEWS = [
  { id: "calendar", label: "Calendar", file: "calendar-month.jpg" },
  { id: "whos-free", label: "Who's free?", file: "whos-free.jpg" },
  { id: "today", label: "What's left of today", file: "whats-left-today.jpg" },
  { id: "mood", label: "Mood at a glance", file: "mood-tiles.jpg" },
  { id: "display", label: "Display Mode", file: "display-mode.jpg" },
  { id: "event", label: "Event detail", file: "event-detail.jpg" },
  { id: "list", label: "Shared list", file: "packing-list.jpg" },
];

function imgSrc(viewId) {
  const v = ROTATOR_VIEWS.find((x) => x.id === viewId);
  return `/landing/${v ? v.file : ROTATOR_VIEWS[0].file}`;
}

function DashboardRotator() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = React.useRef(null);
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % ROTATOR_VIEWS.length);
    }, 4000);
    return () => clearInterval(t);
  }, [paused]);
  // Preload every screenshot once on mount so swapping `src` is
  // instant — without this, the browser only fetches the next image
  // after we point `src` at it, leaving a 200–800 ms gap on slow
  // connections that reads as "the preview disappeared."
  useEffect(() => {
    for (const v of ROTATOR_VIEWS) {
      const im = new Image();
      im.src = imgSrc(v.id);
    }
  }, []);
  const view = ROTATOR_VIEWS[idx].id;
  const next = () => setIdx((i) => (i + 1) % ROTATOR_VIEWS.length);
  const prev = () => setIdx((i) => (i - 1 + ROTATOR_VIEWS.length) % ROTATOR_VIEWS.length);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    const start = touchStartX.current;
    touchStartX.current = null;
    if (start == null) return;
    const end = e.changedTouches[0]?.clientX ?? start;
    const dx = end - start;
    // Only count swipes that are clearly horizontal and not accidental taps.
    if (Math.abs(dx) < 40) return;
    if (dx < 0) next();
    else prev();
  };
  return (
    <div
      className="rounded-[20px] border border-[color:var(--line)] bg-[color:var(--bg-soft,#F5EFE2)] overflow-hidden"
      data-testid="landing-demo-cta"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <style>{`@keyframes ff-fade-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }`}</style>
      <div
        className="px-4 md:px-6 py-2.5 flex items-center justify-end gap-3 border-b border-[color:var(--line-soft)] bg-[color:var(--surface)]"
        data-testid="landing-demo-banner"
      >
        <div className="flex items-center gap-1.5" role="tablist" aria-label="Dashboard mode">
          {ROTATOR_VIEWS.map((v, i) => (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={i === idx}
              aria-label={`Preview ${v.label} dashboard`}
              onClick={() => setIdx(i)}
              className={`h-2 rounded-full transition-all ${
                i === idx
                  ? "w-6 bg-[color:var(--ink)]"
                  : "w-2 bg-[color:var(--ink)]/25 hover:bg-[color:var(--ink)]/40"
              }`}
              data-testid={`landing-demo-tab-${v.id}`}
            />
          ))}
        </div>
      </div>
      <div
        className="px-0 py-0 md:px-1.5 md:py-1.5 flex items-center justify-center bg-[color:var(--bg)]"
      >
        {/* Layered images: every screenshot stays in the DOM, only
            the active one has opacity-100. No remount = no flash;
            preloaded above so first paint of every tab is instant. */}
        <div className="relative w-full">
          {/* Spacer to define the box height — invisible, but keeps the
              container height stable while images crossfade. Uses the
              same source the visible <img> would use, so the box always
              matches the active aspect ratio. */}
          <img
            src={imgSrc(view)}
            alt=""
            aria-hidden="true"
            className="block w-full h-auto opacity-0 pointer-events-none"
          />
          {ROTATOR_VIEWS.map((v) => (
            <img
              key={v.id}
              src={imgSrc(v.id)}
              alt={`FamFeel dashboard preview ${v.label}`}
              className={`absolute inset-0 w-full h-full object-contain object-center transition-opacity duration-300 ease-out ${
                v.id === view ? "opacity-100" : "opacity-0"
              }`}
              loading="eager"
              decoding="async"
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  // Live "people online right now" badge for the hero. Deterministic
  // random-walk simulation — not a real presence count:
  //   • Initial value is seeded by the current hour-of-day so
  //     multiple visitors within the same hour see roughly the same
  //     number (peaks around evenings, low around 03-06).
  //   • Every 5-9s the visible count drifts by ±1-3 (never jumps
  //     from 10 → 458). Small steps keep it feeling live.
  //   • A slowly-drifting ceiling floats between ~400 and ~550 so
  //     the "loft" itself varies — no hard cap at any single number.
  //   • Floor is 10 so the number never dips into a discouraging
  //     single-digit range even at 4am.
  const pickInitial = () => {
    const hour = new Date().getHours();
    // Bell curve peaking at 20:00, low at 04:00. Total range: ~15
    // (deep night) up to ~120 (evening peak). Kept intentionally
    // modest — a small "cozy" live number reads more credible than
    // a big one for a new product.
    const distanceFromPeak = Math.min(
      Math.abs(hour - 20),
      Math.abs(hour + 24 - 20),
      Math.abs(hour - 20 + 24)
    );
    const peakFactor = Math.max(0, 1 - distanceFromPeak / 10);
    const base = 15 + Math.floor(Math.random() * 20);
    const boost = Math.floor(peakFactor * 80);
    return base + boost;
  };
  const [onlineCount, setOnlineCount] = useState(pickInitial);
  const [ceiling, setCeiling] = useState(() => 120 + Math.floor(Math.random() * 60));
  useEffect(() => {
    // Random walk ±1-3 every 2-4s — quick enough that a visitor
    // reading the hero sees at least one tick before scrolling.
    const stepIv = setInterval(() => {
      setOnlineCount((n) => {
        const step = Math.floor(Math.random() * 3) + 1;
        const naturalTarget = pickInitial();
        const sign = n < naturalTarget ? (Math.random() < 0.65 ? 1 : -1)
                   : n > naturalTarget ? (Math.random() < 0.65 ? -1 : 1)
                   : Math.random() < 0.5 ? -1 : 1;
        const next = n + sign * step;
        return Math.max(8, Math.min(ceiling, next));
      });
    }, 2000 + Math.floor(Math.random() * 2000));
    // Ceiling drifts slowly (±3 every 15s, held inside 100-180).
    const ceilIv = setInterval(() => {
      setCeiling((c) => {
        const delta = Math.random() < 0.5 ? -3 : 3;
        return Math.max(100, Math.min(180, c + delta));
      });
    }, 15000);
    return () => { clearInterval(stepIv); clearInterval(ceilIv); };
  }, [ceiling]);

  useSeo({
    title: "FamFeel – Family Check-In & Shared Family Calendar",
    description:
      "FamFeel is the family check-in app for shared households — and a shared family calendar your kids can actually read. Log sleep, mood and what's on this week on one dashboard. Private by default.",
    path: "/",
    jsonLdId: "ld-landing",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "SoftwareApplication",
          "name": "FamFeel – Family Check-In",
          "alternateName": [
            "FamFeel",
            "Family feel",
            "Family Check In",
            "Family Checkin",
            "Family Mood Check",
            "Family Mood Board",
            "Family Mood Log",
            "Home Check In",
            "Home Mood",
          ],
          "applicationCategory": "LifestyleApplication",
          "operatingSystem": "Web, iOS, Android (PWA)",
          "keywords":
            "family check-in app, shared family dashboard, family mood tracker, kitchen iPad family app, Cozi alternative, Google Calendar alternative for families, Life360 alternative, BrightWheel alternative, calm family app, no login family app, toddler lockable family app, shared calendar for kids, blended family app, co-parenting app, family PWA, private family app, kid-friendly family dashboard, family pet care log, dog walk tracker for families, shared family iPad",
          "description":
            "A calm shared family check-in app. Log sleep, mood and what kind of day everyone's having on one household dashboard. Works on any phone, tablet or shared iPad. Private by default — no email, no ads.",
          "offers": [
            { "@type": "Offer", "name": "Free", "description": "Up to 4 members · walk log + care log for 1 dog or cat", "price": "0", "priceCurrency": "USD" },
            { "@type": "Offer", "name": "Plus monthly", "description": "Up to 6 members, custom photo avatars, photo attachments on events, Google/iCloud/Outlook calendar sync, walk log + care log for 2 dogs or cats", "price": "3.99", "priceCurrency": "USD", "priceSpecification": { "@type": "UnitPriceSpecification", "billingIncrement": 1, "unitCode": "MON" } },
            { "@type": "Offer", "name": "Plus yearly", "description": "Up to 6 members, custom photo avatars, photo attachments on events, Google/iCloud/Outlook calendar sync, walk log + care log for 2 dogs or cats — saves ~17%", "price": "39.99", "priceCurrency": "USD", "priceSpecification": { "@type": "UnitPriceSpecification", "billingIncrement": 1, "unitCode": "ANN" } },
            { "@type": "Offer", "name": "Grand monthly", "description": "Up to 10 members, everything in Plus, walk log + care log for 3 dogs or cats, Houses for blended/multi-address families", "price": "6.99", "priceCurrency": "USD", "priceSpecification": { "@type": "UnitPriceSpecification", "billingIncrement": 1, "unitCode": "MON" } },
            { "@type": "Offer", "name": "Grand yearly", "description": "Up to 10 members, everything in Plus, pet care log for 3 dogs or cats, Houses for blended/multi-address families — saves ~20%", "price": "66.99", "priceCurrency": "USD", "priceSpecification": { "@type": "UnitPriceSpecification", "billingIncrement": 1, "unitCode": "ANN" } },
            { "@type": "Offer", "name": "Extended Family monthly", "description": "Up to 20 members, everything in Grand, for very large blended or multi-generational households", "price": "9.99", "priceCurrency": "USD", "priceSpecification": { "@type": "UnitPriceSpecification", "billingIncrement": 1, "unitCode": "MON" } },
            { "@type": "Offer", "name": "Extended Family yearly", "description": "Up to 20 members, everything in Grand, for very large blended or multi-generational households — saves ~20%", "price": "95.99", "priceCurrency": "USD", "priceSpecification": { "@type": "UnitPriceSpecification", "billingIncrement": 1, "unitCode": "ANN" } },
          ],
          "featureList": [
            "Shared family check-ins (sleep, mood, status, notes)",
            "Mood + sleep history (14 days on Free, full range on Plus and above)",
            "Toddler mode — lock the iPad to one little human, big picture tiles, tap-for-surprise screen",
            "Long-press any member to peek at their last 7 days of mood",
            "Pet walk planner with upcoming walk scheduling",
            "Pet care log (vet, flea/tick, bath, litter) for dogs and cats",
            "Shared family calendar and birthdays",
            "Houses for blended, split and multi-address families (Extended) — each house has its own door (House PIN) on the family dashboard",
            "Grandparent read-only view link",
            "Kitchen display / ambient iPad mode",
            "Morning and family-nudge push notifications",
            "Birthday push reminders (Plus, Grand and Extended)",
            "Custom photo avatars (Plus, Grand and Extended Family)",
            "Client-side end-to-end encrypted private notes",
            "No sign-in between people — join code + PIN",
            "Kitchen Display mode — an always-on ambient dashboard for the fridge-door iPad, with greeting, time, every family member and upcoming events, and no notifications",
            "Houses (Grand & Extended tiers) — group members under Mum's, Dad's, Grandparents' with a per-house PIN lock; the family dashboard shows house 'doorbell' tiles and a shared row for kids/pets visible to all houses. Check-ins, events and profiles inside a house need its PIN on this device. Events with an adult tagged from another house surface in both houses (meetings across houses), and moving an adult or teen between houses still requires their own personal PIN.",
            "Birthdays, private notes and gentle nudges — birthdays appear automatically from each person's date of birth, private notes are encrypted on-device, and a 'nudge' push gently prompts anyone who hasn't checked in yet today",
            "Shared family calendar — football practice, doctor's appointments, Grandpa's birthday, the Saturday day trip — add an event, tag who's going, and every phone and the kitchen iPad reflects it instantly; no Google Calendar login required for any child",
            "Big sliders for small kids — young children (under 7) see oversized mood sliders, giant sleep-quality sliders and photo tiles they can tap unaided, no reading skills needed; a 4-year-old can log their morning by themselves",
            "Dog walks and flea/tick treatments — stamp each dog walk with who walked her, log feeds and fresh water, keep a rolling Care log for the annual vet exam, flea/tick treatment and bath so nothing ever gets forgotten across busy households",
          ],
          "audience": {
            "@type": "PeopleAudience",
            "audienceType":
              "People usually start FamFeel when: a kid just turned 11 or 12 and has a whole inner world they don't narrate at dinner; kids are at different ages (3, 7, 12, 15) and nobody can hold the whole week in their head; parents separated and the kids now live across two houses that need to stay gently in sync; a grandparent moved in or nearby and is part of the daily rhythm but not on the WhatsApp group; a family got a dog or a cat and 'has the dog been out?' is suddenly asked six times a day.",
          },
        },
        {
          "@type": "Organization",
          "name": "FamFeel",
          "url": typeof window !== "undefined" ? window.location.origin : undefined,
        },
      ],
    },
  });

  // If arriving from a QR scan (?join=XXX), skip marketing and go straight to the app
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("join")) {
      navigate(`/app${location.search}`, { replace: true });
    }
  }, [location, navigate]);

  // Landing-visit ping now lives inside MarketingLayout so every public
  // page counts, not just this one — no-op here.

  return (
    <MarketingLayout>
      <section className="px-6 md:px-10 pt-12 md:pt-20 pb-16 md:pb-28" data-reveal>
        <div className="max-w-6xl grid md:grid-cols-[1.15fr_0.85fr] gap-6 md:gap-14 items-center">
          <div>
            <div className="label-tiny mb-5" data-reveal data-testid="landing-hero-eyebrow">
              {t({
                en: "The",
                da: "Fælles kalender for hele familien",
              })}
            </div>
            <h1
              className="font-hero-display text-[13vw] md:text-[7.25rem] leading-[0.92] tracking-tight mb-3"
              data-reveal
              style={{ transitionDelay: "60ms" }}
              data-testid="landing-h1"
            >
              {lang === "da" ? (
                <>
                  Alt i samme
                  <br />
                  kalender
                </>
              ) : (
                "Shared calendar"
              )}
            </h1>
            {/* English-only sub-line — reads with the eyebrow + H1 to
                complete the sentence: "The · Shared calendar · every
                family needs." Danish keeps its two-line editorial hero
                with no sub-line, so we skip this block entirely. */}
            {lang !== "da" && (
              <div
                className="font-display-italic text-2xl md:text-4xl text-[color:var(--ink-2)] tracking-tight mb-8"
                data-reveal
                style={{ transitionDelay: "100ms" }}
                data-testid="landing-h1-subline"
              >
                every family needs.
              </div>
            )}
            <p
              className="font-body-editorial text-xl md:text-2xl text-[color:var(--ink-2)] max-w-2xl leading-relaxed mb-8"
              data-reveal
              style={{ transitionDelay: "140ms" }}
            >
              {t({
                en: "Football practice, grandpa's birthday and much more. With FamFeel everyone knows the plans and who's going where.",
                da: "Fodboldtræning, bedstefars fødselsdag og meget mere. Med FamFeel kender alle planerne og hvem der skal med til hvad.",
              })}
            </p>
            <div
              className="flex flex-wrap items-center gap-3"
              data-reveal
              style={{ transitionDelay: "220ms" }}
            >
              <Link
                to="/app?mode=create"
                className="ff-btn ff-btn-primary text-base px-6 py-3"
                data-testid="hero-cta"
              >
                {t({
                  en: "Start free for 4 · no email needed",
                  da: "Start gratis, ingen mails nødvendig",
                })}
              </Link>
              <Link to="/faq" className="ff-btn text-base" data-testid="hero-faq">
                {t({ en: "How does it work?", da: "Hvordan virker det?" })}
              </Link>
            </div>
            <div className="label-tiny mt-6 text-[color:var(--ink-3)] whitespace-nowrap">
              {t({
                en: "Free for 4 · No ads · Easy setup",
                da: "Gratis for 4 · Ingen reklamer · Nem opsætning",
              })}
            </div>
            {/* Social-proof badge — "N people online right now" style.
                The count is a random-walk simulation (see onlineCount
                logic above) so it drifts naturally in small ±1-3 steps
                rather than jumping. Above the fold; no data-reveal so
                it never gets stuck at opacity 0 if the state
                hydrates after the IntersectionObserver scan. */}
            <div
              className="label-tiny mt-3 inline-flex items-center gap-2 text-[color:var(--ink-2)] whitespace-nowrap"
              data-testid="landing-social-proof"
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--accent,#7a8c69)] animate-pulse"
                aria-hidden="true"
              />
              <span data-testid="landing-social-proof-count">
                {t({
                  en: `${onlineCount} people online right now`,
                  da: `${onlineCount} personer online lige nu`,
                })}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Feature rows — calendar-led ordering. Mood/check-in is now a
          softer "also does" further down the page so the lead story
          stays squarely on the shared family calendar. */}
      <section className="px-6 md:px-10 pb-16 md:pb-24 max-w-5xl" data-reveal>
        <Feature
          eyebrow={t({ en: "The shared family calendar", da: "Den fælles familiekalender" })}
          title={t({
            en: "One calendar everyone actually uses.",
            da: "Én kalender alle i familien kan bruge",
          })}
          body={t({
            en: "Add it once, it shows up for everyone. Tag who's involved — they get the heads-up. No extra apps. No \u201cI didn't know.\u201d",
            da: "Tilføj én gang — det popper op hos alle. Markér hvem der skal med, så får de besked. Ingen ekstra apps. Ingen \u201cdet vidste jeg ikke\u201d.",
          })}
        />
        <Feature
          eyebrow={t({ en: "Easy access for the whole family", da: "Nem adgang for hele familien" })}
          title={t({
            en: "PIN + code — dead simple.",
            da: "Pin + kode, gør det hele nemt",
          })}
          body={t({
            en: "No email, no forgotten passwords. Your family gets one unique join code and PIN — every device stays signed in effortlessly.",
            da: "Ingen e-mail eller glemte passwords. I får en unik familie-kode og pinkode der gør det nemt for alle i familien at forblive logget ind.",
          })}
        />
        <Feature
          eyebrow={t({ en: "Kitchen display · shared devices", da: "Køkken-display + Telefoner" })}
          title={t({
            en: "A calm dashboard for the whole house.",
            da: "Overblikket for hele huset",
          })}
          body={t({
            en: "Turn any shared device into a family display — everyone lives on the same dashboard. Easy for the kids to see what's coming.",
            da: "Lav en fælles enhed til familiens display — alle har det samme overblik. Nemt for børnene at se, hvad der skal ske.",
          })}
        />
      </section>

      {/* Mini demo — static preview of the dashboard */}
      <section className="px-6 md:px-10 pb-16 md:pb-24" data-testid="landing-demo" data-reveal>
        <div className="max-w-[420px] md:max-w-[460px]">
          <div className="label-tiny mb-5">
            {t({ en: "A peek inside FamFeel", da: "Et kig ind i FamFeel" })}
          </div>
          <DashboardRotator />
        </div>
      </section>

      {/* Also included — one-line feature reel that sits directly under
          the rotator. Editorial two-column list on desktop, single
          column on phones. Each row: bold noun · em-dash · muted
          one-line description. Reads as a quick catalog of everything
          bundled in a FamFeel workspace so a visitor doesn't have to
          scroll for the answer to "what does it actually do?". */}
      <section
        className="px-6 md:px-10 pb-20 md:pb-28 max-w-5xl"
        data-testid="landing-also-included"
        data-reveal
      >
        <div className="label-tiny mb-6">
          {t({ en: "Also included", da: "Også en del af FamFeel" })}
        </div>
        <ul className="grid md:grid-cols-2 gap-x-10 gap-y-5">
          {[
            {
              name: { en: "Lists", da: "Lister" },
              desc: {
                en: "Make lists for shopping, baking or packing.",
                da: "Lav lister til indkøb, bagning eller pakning.",
              },
            },
            {
              name: { en: "Chores", da: "Pligter" },
              desc: {
                en: "Set up chores the kids can tick off.",
                da: "Lav pligter børnene kan sætte flueben ved.",
              },
            },
            {
              name: { en: "Who’s free?", da: "Hvem er fri?" },
              desc: {
                en: "See who’s available on a certain day.",
                da: "Se hvem der er ledig en bestemt dag.",
              },
            },
            {
              name: { en: "Pet care", da: "Kæledyr" },
              desc: {
                en: "Keep track of walks, feeds and vet visits.",
                da: "Hold styr på gåture, foder og dyrlægebesøg.",
              },
            },
            {
              name: { en: "Mood log", da: "Humør-log" },
              desc: {
                en: "Let the family log their mood in one tap.",
                da: "Lad familien logge deres humør med ét tryk.",
              },
            },
          ].map((f, i) => (
            <li
              key={i}
              className="flex flex-wrap gap-x-2 items-baseline border-b border-[color:var(--line-soft)] pb-4"
            >
              <span className="font-display text-lg md:text-xl tracking-tight text-[color:var(--ink)]">
                {t(f.name)}
              </span>
              <span className="text-[color:var(--ink-3)]">—</span>
              <span className="text-[color:var(--ink-2)] leading-snug text-[15px] flex-1 min-w-[140px]">
                {t(f.desc)}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* Remaining feature rows — now empty (Bring calendar, Shared
          lists both removed 2026-07). Keeping the wrapper commented so
          it's clear where to slot future secondary features. */}

      {/* Comparison block removed (2026-04) — copy felt repetitive
          alongside the feature stack and pricing. The "Cozi /
          Google Calendar alternative" SEO terms still live in the
          page's keywords meta + the H1/H2 copy, so we keep crawler
          coverage without the user-facing wall of text. */}

      {/* Pricing — same numbers as /pets, framed around the household
          instead of the pet count. Keeps the reader from bouncing to
          /pets just to check cost. Danish version rendered LATER
          (after the install-as-app card) so Danish visitors first see
          the value story, then the price. */}
      {lang === "da" ? null : (
      <section className="px-6 md:px-10 pb-16 md:pb-28 max-w-3xl" data-reveal>
        <div className="label-tiny mb-3">{t({ en: "Pricing", da: "Priser" })}</div>
        <h2 className="font-display-italic text-2xl md:text-3xl leading-[1.05] tracking-tight mb-6">
          {t({
            en: "Start free. Scale when the house gets bigger.",
            da: "Start gratis. Skalér op når huset vokser.",
          })}
        </h2>
        <div className="ff-card p-5 md:p-6" data-testid="landing-pricing">
          <PricingRow
            name="Free"
            priceLarge="$0"
            priceSmall=""
            capacity={t({
              en: "4 members · For the core 4 · 50 events at a time",
              da: "4 medlemmer · For kernefamilien · 50 begivenheder ad gangen",
            })}
          />
          <PricingRow
            name="Plus"
            priceLarge="$3.99"
            priceSmall={t({ en: "/mo · or $39.99/yr (save $8)", da: "/md · eller $39.99/år (spar $8)" })}
            capacity={t({
              en: "6 members · photo avatars · photo attachment · unlimited events · Sync calendars",
              da: "6 medlemmer · foto-avatarer · foto-vedhæftninger · ubegrænset antal begivenheder · kalender-synk",
            })}
            highlight
            badge={t({ en: "Most families pick this", da: "De fleste familier vælger denne" })}
          />
          <PricingRow
            name="Grand"
            priceLarge="$6.99"
            priceSmall={t({ en: "/mo · or $66.99/yr (save $17)", da: "/md · eller $66.99/år (spar $17)" })}
            capacity={t({
              en: "10 members · everything in Plus · care log for 3 pets · Houses for co-parenting",
              da: "10 medlemmer · alt i Plus · pleje-log til 3 kæledyr · Huse til delefamilier",
            })}
          />
          <PricingRow
            name="Extended Family"
            priceLarge="$9.99"
            priceSmall={t({ en: "/mo · or $95.99/yr (save $24)", da: "/md · eller $95.99/år (spar $24)" })}
            capacity={t({
              en: "20 members · care log for unlimited pets · for very large, multi-generational use",
              da: "20 medlemmer · pleje-log til alle jeres kæledyr · til meget store, tre-generationers hjem",
            })}
          />
        </div>
        <p className="label-tiny text-[color:var(--ink-3)] mt-3">
          {t({
            en: "Yearly plans save 20%. Pets count as members. Cancel any time.",
            da: "Årlige planer sparer 20%. Kæledyr tæller som medlemmer. Opsig når som helst.",
          })}
        </p>
      </section>
      )}

      {/* Trust-building manifesto — the "Optional · check-ins" and
          "Funded by paying members" Features that used to live here
          were removed 2026-07 to tighten the flow. The manifesto is
          now covered inside the pricing/testimonials pair. */}

      {/* Life-stage hook moved to JSON-LD only (search engines still get
          the "when people start FamFeel" cues via audience.audienceType)
          so the visible page stays tighter above the install card. */}

      {/* Install-as-app card — sits between the pricing/manifesto block
          and the testimonial rotator so the reader moves from plans →
          "runs like an app" → proof. */}
      <section className="px-6 md:px-10 pb-16 md:pb-24 section-warm" data-testid="landing-install" data-reveal>
        <div className="max-w-5xl">
          <div className="rounded-[20px] border border-[color:var(--line)] bg-[color:var(--surface)] p-6 md:p-8 flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
            <div className="max-w-xl">
              <div className="label-tiny mb-2">
                {t({ en: "Installs like an app", da: "Installeres som en app" })}
              </div>
              <h3 className="font-display-italic text-2xl md:text-3xl leading-[0.98] tracking-tight mb-3">
                {t({
                  en: "Works on all devices. Add to Home Screen.",
                  da: "Virker på alle enheder. Nem adgang via hjemmeskærmen.",
                })}
              </h3>
              <p className="text-[color:var(--ink-2)] text-base md:text-lg leading-snug">
                {t({
                  en: "No App Store, no waiting for updates. One tap, gets its own icon. Push notifications. We'll show you how it works.",
                  da: "Ingen App Store, ingen ventetid på opdateringer. Ét tryk — appen får sit eget ikon. Push-notifikationer. Vi viser dig hvordan.",
                })}
              </p>
            </div>
            <div className="flex-shrink-0 flex items-center gap-3 self-start md:self-center">
              <img
                src="/icon-512.png"
                alt="FamFeel app icon"
                width="96"
                height="96"
                loading="lazy"
                className="w-20 h-20 md:w-24 md:h-24 rounded-[16px] shadow-sm border border-[color:var(--line-soft)]"
              />
              <div className="label-tiny text-[color:var(--ink-3)] max-w-[140px] leading-tight">
                {t({
                  en: "Looks like this on your home screen.",
                  da: "Sådan ser appen ud på din home screen.",
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Danish pricing — placed AFTER the install-as-app card so
          Danish visitors first see the calm value story, then arrive
          at the price paragraph. English site keeps its plan-card grid
          earlier in the flow. */}
      {lang === "da" && (
        <section className="px-6 md:px-10 pb-16 md:pb-24 max-w-3xl" data-reveal>
          <div className="label-tiny mb-3">Priser</div>
          <h2 className="font-display-italic text-2xl md:text-3xl leading-[1.05] tracking-tight mb-6">
            Betalt af familier, ikke reklamer
          </h2>
          <p className="text-[color:var(--ink-2)] leading-relaxed">
            Gratis for 4. Kun 3,99$ for op til 6. 6,99$ for op til 10.
            Opskaler hvis familien vokser. Årlige planer sparer 20%.
            Kæledyr tæller som medlemmer. Opsig når som helst.
            Slet alt data når som helst.
          </p>
        </section>
      )}

      {/* Testimonials — compact rotator. Quietly cycles 16 reviews
          from the markets that match the flag row in the hero. The
          full set lives at /reviews with Schema.org JSON-LD. Moved
          to the end of the page (just before the closing CTA) so the
          pricing + trust manifesto block gets breathing room, and the
          social-proof reinforcement lands at the moment of decision. */}
      <TestimonialRotator />

      {/* Closing CTA */}
      <section className="px-6 md:px-10 pt-16 md:pt-24 pb-24" data-reveal>
        <div className="max-w-3xl">
          <h2 className="font-display-italic text-5xl md:text-6xl leading-[1.0] mb-6 tracking-tight">
            {t({ en: "Try it today.", da: "Start nu, det er nemt." })}
          </h2>
          <p className="text-xl text-[color:var(--ink-2)] mb-8">
            {t({
              en: "Takes about three minutes to set up.",
              da: "Tager cirka tre minutter at sætte op.",
            })}
          </p>
          <Link
            to="/app?mode=create"
            className="ff-btn ff-btn-primary text-base px-6 py-3"
            data-testid="closing-cta"
          >
            {t({ en: "Start your family →", da: "Start din familie →" })}
          </Link>
          {/* Trust pills — live next to the closing CTA so the signal
              lands at the moment of decision, not buried in the footer.
              Editorial paper pills, no icons, no divider line. */}
          <div
            className="mt-8 flex flex-wrap gap-2 justify-center md:justify-start"
            data-testid="closing-trust-strip"
          >
            {[
              { en: "E2E encrypted notes", da: "E2E-krypterede noter" },
              { en: "GDPR compliant", da: "GDPR-compliant" },
              { en: "99.9% uptime", da: "99,9% oppetid" },
              { en: "Live on Stripe", da: "Betalinger via Stripe" },
            ].map((pill) => (
              <span
                key={pill.en}
                className="label-tiny rounded-full px-3 py-1 bg-[color:var(--bg-soft)] border border-[color:var(--line-soft)] text-[color:var(--ink-2)]"
              >
                {t(pill)}
              </span>
            ))}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
